import React, { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as XLSX from "xlsx";
import { UploadCloud, FileSpreadsheet, X, AlertCircle } from "lucide-react";
import { CHAIN_TEST_SHEET_ACCEPT, CHAIN_TEST_SHEET_MAX_BYTES } from "../../../services/diagnosticLabsService";

const ALLOWED = CHAIN_TEST_SHEET_ACCEPT.split(",");
const fmtSize = (bytes) => (bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`);

// Reads the first sheet locally for a quick preview (row count + first rows).
// This is a preview only — the backend parser is the source of truth.
const readPreview = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(new Uint8Array(e.target.result), { type: "array", sheetRows: 2001 });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false, defval: "" });
        resolve({ header: rows[0] || [], rows: rows.slice(1, 6), dataRows: Math.max(0, rows.length - 1), capped: rows.length > 2000 });
      } catch {
        resolve(null);
      }
    };
    reader.onerror = () => resolve(null);
    reader.readAsArrayBuffer(file);
  });

export const validateSheetFile = (file) => {
  if (!file) return "Choose a file.";
  const ext = `.${file.name.split(".").pop().toLowerCase()}`;
  if (!ALLOWED.includes(ext)) return "Use an .xlsx or .csv file (legacy .xls isn't supported — save it as .xlsx).";
  if (file.size > CHAIN_TEST_SHEET_MAX_BYTES) return "File must be 10 MB or smaller.";
  return "";
};

const FileDropzone = ({ file, onChange, disabled = false }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);

  const pick = async (next) => {
    if (!next) return;
    const err = validateSheetFile(next);
    setError(err);
    if (err) return;
    onChange(next);
    setPreview(await readPreview(next));
  };

  const clear = () => {
    onChange(null);
    setPreview(null);
    setError("");
  };

  return (
    <div>
      <AnimatePresence mode="wait" initial={false}>
        {!file ? (
          <motion.label
            key="drop"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}
            onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); if (!disabled) pick(e.dataTransfer.files?.[0]); }}
            className={`flex flex-col items-center justify-center gap-2 px-6 py-9 rounded-2xl border-2 border-dashed text-center cursor-pointer transition-colors ${
              dragging ? "border-[#00A6A6] bg-[#E6F6F6]" : "border-[#CFE0E0] bg-white hover:border-[#00A6A6]/60 hover:bg-[#F5FAFA]"
            } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
          >
            <motion.span animate={{ y: dragging ? -3 : 0 }} className="w-11 h-11 rounded-xl bg-[#E6F6F6] flex items-center justify-center">
              <UploadCloud className="w-5 h-5 text-[#00A6A6]" />
            </motion.span>
            <span className="text-sm font-medium text-gray-800">
              Drop the test list here or <span className="text-[#0F4C81] underline underline-offset-2">browse</span>
            </span>
            <span className="text-xs text-gray-400">XLSX or CSV · up to 10 MB · same columns as the lab test template</span>
            <input
              ref={inputRef}
              type="file"
              accept={CHAIN_TEST_SHEET_ACCEPT}
              className="hidden"
              disabled={disabled}
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; pick(f); }}
            />
          </motion.label>
        ) : (
          <motion.div key="file" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="rounded-2xl border border-[#DCEAEA] bg-white overflow-hidden">
            <div className="flex items-center gap-3 px-4 py-3">
              <span className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                <p className="text-xs text-gray-400">
                  {fmtSize(file.size)}
                  {preview && ` · ${preview.capped ? "2,000+" : preview.dataRows.toLocaleString("en-IN")} data rows`}
                </p>
              </div>
              {!disabled && (
                <button type="button" onClick={clear} aria-label="Remove file"
                  className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {preview && preview.header.length > 0 && (
              <div className="border-t border-[#E9F2F2]">
                <p className="px-4 pt-2.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                  Preview · validation runs on upload
                </p>
                <div className="overflow-x-auto px-4 pb-3 pt-1.5">
                  <table className="min-w-full text-[11px]">
                    <thead>
                      <tr>
                        {preview.header.slice(0, 6).map((h, i) => (
                          <th key={i} className="text-left font-medium text-gray-500 pr-4 pb-1 whitespace-nowrap">{String(h)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((row, r) => (
                        <tr key={r} className="border-t border-gray-50">
                          {preview.header.slice(0, 6).map((_, c) => (
                            <td key={c} className="pr-4 py-1 text-gray-700 whitespace-nowrap max-w-[160px] truncate">{String(row[c] ?? "")}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      {error && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-red-600">
          <AlertCircle className="w-3.5 h-3.5" /> {error}
        </p>
      )}
    </div>
  );
};

export default FileDropzone;
